import { useState } from "react";

import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Select from "../../../shared/ui/Field/Select";
import Textarea from "../../../shared/ui/Field/Textarea";
import { clientOptionLabel } from "../api/clientSearch";
import {
  LABEL_MAX_LENGTH,
  MEDIA_CEILING,
  NOTE_MAX_LENGTH,
} from "../lib/publicProjection";
import {
  DEFAULT_EXPIRY_PRESET,
  EXPIRY_PRESETS,
  resolveDraftExpiresAt,
  toDatetimeLocalValue,
  validateExpiryIso,
} from "../lib/expiry";
import { LOCATION_PRECISION_LABELS, PRICE_MODE_LABELS } from "../lib/labels";
import { WIZARD_STAGES, WizardProgress, pickStageErrors } from "../lib/wizard";
import PresentationClientCombobox from "./PresentationClientCombobox";
import PresentationMediaPicker from "./PresentationMediaPicker";
import PresentationToggle from "./PresentationToggle";

export { WIZARD_STAGES, WIZARD_PROGRESS, WizardProgress, STAGE_FIELD_KEYS } from "../lib/wizard";

const PRICE_OPTIONS = Object.entries(PRICE_MODE_LABELS).map(([value, label]) => ({
  value,
  label,
}));
const LOCATION_OPTIONS = Object.entries(LOCATION_PRECISION_LABELS).map(([value, label]) => ({
  value,
  label,
}));
const CURRENCY_OPTIONS = [
  { value: "UAH", label: "UAH" },
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
];

const LOCATION_PRECISION_HELP = {
  area:
    "Клієнт бачитиме лише район або населений пункт. Точна адреса, карта та кнопка Google Maps не відображатимуться.",
  street:
    "Клієнт бачитиме вулицю без номера. Карта та кнопка Google Maps не відображатимуться.",
  exact:
    "Клієнт бачитиме точну адресу. Карта та кнопка Google Maps відображатимуться, якщо для об'єкта є координати.",
};

const MEDIA_REORDER_HINT =
  "Перетягніть елементи за маркер, щоб змінити порядок фото в презентації.";

export function locationPrecisionHelp(precision) {
  return LOCATION_PRECISION_HELP[precision] || LOCATION_PRECISION_HELP.area;
}

function propertyImages(property) {
  return Array.isArray(property && property.images)
    ? property.images.filter((img) => img && img.id)
    : [];
}

function defaultMediaIds(property) {
  return propertyImages(property).slice(0, MEDIA_CEILING).map((img) => img.id);
}

function PresentationInputAction({
  id,
  "aria-describedby": describedBy,
  "aria-invalid": invalid,
  type = "text",
  value,
  onChange,
  showAction,
  onAction,
  actionLabel = "Використати з профілю",
  readOnly = false,
  "data-testid": testId,
}) {
  return (
    <div className="presentation-input-action" data-testid="presentation-input-action">
      <TextInput
        id={id}
        type={type}
        value={value}
        readOnly={readOnly}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        data-testid={testId}
        onChange={onChange}
      />
      {showAction ? (
        <button
          type="button"
          className="action-btn presentation-profile-assist"
          onClick={onAction}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

export function buildEditorDraft({ share, property, ownerContactPrefill, priceLocked = false }) {
  if (share) {
    const selected = Array.isArray(share.media_selection)
      ? [...share.media_selection]
          .sort((a, b) => a.position - b.position)
          .map((row) => row.image_id)
      : [];
    const label = share.internal_label || "";
    return {
      client: share.client || null,
      internal_label: label,
      internal_label_original: label,
      internal_label_dirty: false,
      price_mode: priceLocked ? "property_price" : (share.price_mode || "property_price"),
      priceLocked: Boolean(priceLocked),
      custom_price: priceLocked ? "" : (share.custom_price || ""),
      custom_currency: priceLocked
        ? (property?.currency || "UAH")
        : (share.custom_currency || property?.currency || "UAH"),
      location_precision: share.location_precision || "area",
      presentation_note: share.presentation_note || "",
      include_video: Boolean(share.include_video),
      contact_name: share.contact?.name || "",
      contact_phone: share.contact?.phone || "",
      contact_email: share.contact?.email || "",
      include_contact_avatar: Boolean(
        share.include_contact_avatar || share.contact?.avatar_url,
      ),
      include_contact_avatar_original: Boolean(
        share.include_contact_avatar || share.contact?.avatar_url,
      ),
      avatar_dirty: false,
      confirm_company_fallback: false,
      expiry_preset: "custom",
      custom_expires_at: toDatetimeLocalValue(share.expires_at),
      expires_at_original: share.expires_at || null,
      expiry_dirty: false,
      mediaIds: selected,
    };
  }
  return {
    client: null,
    internal_label: "",
    internal_label_original: "",
    internal_label_dirty: true,
    price_mode: "property_price",
    priceLocked: Boolean(priceLocked),
    custom_price: "",
    custom_currency: property?.currency || "UAH",
    location_precision: "area",
    presentation_note: "",
    include_video: false,
    contact_name: ownerContactPrefill?.name || "",
    contact_phone: ownerContactPrefill?.phone || "",
    contact_email: ownerContactPrefill?.email || "",
      include_contact_avatar: false,
      include_contact_avatar_original: false,
      avatar_dirty: true,
      confirm_company_fallback: false,
      expiry_preset: DEFAULT_EXPIRY_PRESET,
    custom_expires_at: "",
    expires_at_original: null,
    expiry_dirty: true,
    mediaIds: defaultMediaIds(property),
  };
}

export function buildWritePayload(draft, from = new Date()) {
  const priceLocked = Boolean(draft.priceLocked);
  const priceMode = priceLocked ? "property_price" : draft.price_mode;
  const payload = {
    client: draft.client ? draft.client.id : null,
    price_mode: priceMode,
    location_precision: draft.location_precision,
    presentation_note: draft.presentation_note || "",
    include_video: Boolean(draft.include_video),
    contact_phone: draft.contact_phone || "",
    contact_email: draft.contact_email || "",
    media: (draft.mediaIds || []).map((imageId, position) => ({
      image_id: imageId,
      position,
    })),
  };
  if (draft.internal_label_dirty !== false) {
    payload.internal_label = String(draft.internal_label || "").trim();
  }
  if (draft.expiry_dirty !== false) {
    payload.expires_at = resolveDraftExpiresAt(draft, from);
  }
  if (draft.avatar_dirty !== false) {
    payload.include_contact_avatar = Boolean(draft.include_contact_avatar);
  }
  if (!priceLocked && draft.price_mode === "custom_price") {
    payload.custom_price = draft.custom_price;
    payload.custom_currency = draft.custom_currency;
  } else {
    payload.custom_price = null;
    payload.custom_currency = null;
  }
  return payload;
}

export function validateDraft(draft) {
  const errors = {};
  if (draft.price_mode === "custom_price") {
    const amount = Number(draft.custom_price);
    if (!Number.isFinite(amount) || amount <= 0) {
      errors.custom_price = "Вкажіть додатну власну ціну.";
    }
  }
  if (draft.expiry_dirty !== false) {
    const expiryError = validateExpiryIso(resolveDraftExpiresAt(draft));
    if (expiryError) errors.expires_at = expiryError;
  }
  if ((draft.presentation_note || "").length > NOTE_MAX_LENGTH) {
    errors.presentation_note = `Не більше ${NOTE_MAX_LENGTH} символів.`;
  }
  const label = String(draft.internal_label || "");
  if (label.length > LABEL_MAX_LENGTH) {
    errors.internal_label = `Не більше ${LABEL_MAX_LENGTH} символів.`;
  } else if (draft.internal_label_dirty !== false && !label.trim()) {
    errors.internal_label = "Вкажіть внутрішню назву.";
  }
  return errors;
}

function nextActionLabel(isLastStage, submitLabel) {
  if (isLastStage) return submitLabel;
  return "Далі";
}

function PresentationEditorForm({
  draft,
  onChange,
  property,
  ownerContactPrefill = null,
  fieldErrors = {},
  formError,
  busy,
  onSubmit,
  onCancel,
  submitLabel,
  stageIndex = 0,
  onStageIndexChange,
  priceLocked = false,
  searchClients = null,
  corporateMode = false,
  isEditing = false,
}) {
  const images = propertyImages(property);
  const [stageErrors, setStageErrors] = useState({});
  const [uncontrolledStage, setUncontrolledStage] = useState(0);
  const isControlled = typeof onStageIndexChange === "function";
  const currentStageIndex = isControlled ? stageIndex : uncontrolledStage;
  const setStage = (next) => {
    if (isControlled) onStageIndexChange(next);
    else setUncontrolledStage(next);
  };
  const stage = WIZARD_STAGES[currentStageIndex] || WIZARD_STAGES[0];
  const isLastStage = currentStageIndex >= WIZARD_STAGES.length - 1;
  const errors = { ...fieldErrors, ...stageErrors };
  const profilePhone = ownerContactPrefill?.profilePhone || "";
  const profileEmail = ownerContactPrefill?.profileEmail || "";
  const showUseProfilePhone = Boolean(profilePhone) && draft.contact_phone !== profilePhone;
  const showUseProfileEmail = Boolean(profileEmail) && draft.contact_email !== profileEmail;
  const canEnableAvatar = Boolean(ownerContactPrefill?.hasProfileImage)
    || Boolean(draft.include_contact_avatar);

  const patch = (partial) => onChange({ ...draft, ...partial });

  const handleClientSelect = (client) => {
    if (!client) {
      patch({ client: null });
      return;
    }
    const label = clientOptionLabel(client);
    const next = {
      client: { id: client.id, name: label },
    };
    if (!String(draft.internal_label || "").trim()) {
      next.internal_label = `Для ${label}`;
      next.internal_label_dirty = true;
    }
    patch(next);
  };

  const handleNext = (event) => {
    event.preventDefault();
    const nextErrors = pickStageErrors(validateDraft(draft), stage.id);
    if (Object.keys(nextErrors).length) {
      setStageErrors(nextErrors);
      return;
    }
    setStageErrors({});
    if (isLastStage) {
      onSubmit();
      return;
    }
    setStage(currentStageIndex + 1);
  };

  const handleBack = () => {
    if (currentStageIndex > 0) {
      setStage(currentStageIndex - 1);
      return;
    }
    onCancel();
  };

  return (
    <form
      className="presentation-editor ui-form"
      onSubmit={handleNext}
      noValidate
      data-testid="presentation-editor"
      data-stage={stage.id}
    >
      <WizardProgress currentIndex={currentStageIndex} />

      {formError ? (
        <p className="presentation-form__error" role="alert">
          {formError}
        </p>
      ) : null}

      {stage.id === "recipient" ? (
        <section className="presentation-editor__section">
          <h3>Отримувач</h3>
          <Field
            id="presentation-client-search"
            label="Клієнт CRM"
            help="Прив'язка приватна. Клієнт не бачить свого імені в презентації."
            error={errors.client}
          >
            <PresentationClientCombobox
              selectedClient={draft.client}
              onSelect={handleClientSelect}
              error={errors.client}
              disabled={busy}
              searchClients={searchClients}
            />
          </Field>
          <Field
            id="presentation-internal-label"
            label="Внутрішня назва"
            help="Лише для вас. Не потрапляє в публічну сторінку."
            error={errors.internal_label}
            required
            className="presentation-internal-label-field"
          >
            <TextInput
              value={draft.internal_label}
              maxLength={LABEL_MAX_LENGTH}
              onChange={(event) =>
                patch({
                  internal_label: event.target.value,
                  internal_label_dirty: true,
                })
              }
            />
          </Field>
        </section>
      ) : null}

      {stage.id === "presentation" ? (
        <section className="presentation-editor__section">
          <h3>Презентація</h3>
          {priceLocked || draft.priceLocked ? (
            <Field
              id="presentation-price-mode"
              label="Ціна для клієнта"
              help="Партнерська презентація показує лише ціну об'єкта."
            >
              <p data-testid="presentation-price-locked">
                {PRICE_MODE_LABELS.property_price}
              </p>
            </Field>
          ) : (
            <>
          <Field id="presentation-price-mode" label="Ціна для клієнта" error={errors.price_mode}>
            <Select
              value={draft.price_mode}
              options={PRICE_OPTIONS}
              onChange={(event) => {
                const nextMode = event.target.value;
                patch({
                  price_mode: nextMode,
                  custom_price: nextMode === "custom_price" ? draft.custom_price : "",
                });
              }}
            />
          </Field>
          {draft.price_mode === "custom_price" ? (
            <div className="presentation-editor__row">
              <Field id="presentation-custom-price" label="Власна ціна" error={errors.custom_price} required>
                <TextInput
                  type="number"
                  value={draft.custom_price}
                  onChange={(event) => patch({ custom_price: event.target.value })}
                />
              </Field>
              <Field id="presentation-custom-currency" label="Валюта" error={errors.custom_currency}>
                <Select
                  value={draft.custom_currency}
                  options={CURRENCY_OPTIONS}
                  onChange={(event) => patch({ custom_currency: event.target.value })}
                />
              </Field>
            </div>
          ) : null}
          {draft.price_mode !== "custom_price" && errors.custom_currency ? (
            <p className="presentation-form__error" role="alert">
              {errors.custom_currency}
            </p>
          ) : null}
            </>
          )}
          <Field
            id="presentation-location"
            label="Точність адреси"
            help={locationPrecisionHelp(draft.location_precision)}
            error={errors.location_precision}
          >
            <Select
              value={draft.location_precision}
              options={LOCATION_OPTIONS}
              onChange={(event) => patch({ location_precision: event.target.value })}
            />
          </Field>
          <Field
            id="presentation-note"
            label="Нотатка для клієнта"
            help={`Окремо від опису об'єкта. ${draft.presentation_note.length}/${NOTE_MAX_LENGTH}`}
            error={errors.presentation_note}
          >
            <Textarea
              rows={4}
              maxLength={NOTE_MAX_LENGTH}
              value={draft.presentation_note}
              onChange={(event) => patch({ presentation_note: event.target.value })}
            />
          </Field>
        </section>
      ) : null}

      {stage.id === "media" ? (
        <section className="presentation-editor__section">
          <h3>Медіа</h3>
          <p
            className="presentation-editor__help presentation-editor__media-hint"
            data-testid="presentation-media-reorder-hint"
          >
            <i className="fa-solid fa-grip-vertical" aria-hidden="true" />
            <span>{MEDIA_REORDER_HINT}</span>
          </p>
          {images.length > MEDIA_CEILING ? (
            <p className="presentation-editor__warn" role="note">
              У об'єкта більше {MEDIA_CEILING} файлів. За замовчуванням вибрано перші {MEDIA_CEILING}.
            </p>
          ) : null}
          {(draft.mediaIds || []).length === 0 ? (
            <p className="presentation-editor__warn" role="note">
              Без медіа клієнт не побачить фото.
            </p>
          ) : null}
          <PresentationMediaPicker
            images={images}
            mediaIds={draft.mediaIds || []}
            error={errors.media}
            onChange={(mediaIds) => patch({ mediaIds })}
          />
          <PresentationToggle
            id="presentation-include-video"
            label="Показати відео, якщо воно є на об'єкті"
            help="Якщо на об'єкті є відео, клієнт побачить його в презентації."
            error={errors.include_video}
            checked={Boolean(draft.include_video)}
            onChange={(checked) => patch({ include_video: checked })}
            data-testid="presentation-video-toggle"
          />
        </section>
      ) : null}

      {stage.id === "contact" ? (
        <section className="presentation-editor__section">
          <h3>Контакти і доступ</h3>
          <p className="presentation-editor__help">
            Контакт зберігається як знімок і не зміниться, якщо ви пізніше оновите профіль.
          </p>
          <Field
            id="presentation-contact-name"
            label="Ім'я контакту"
            help="Береться з облікового запису і не редагується вручну."
            error={errors.contact_name}
          >
            <TextInput
              value={draft.contact_name}
              readOnly
              data-testid="presentation-contact-name"
            />
          </Field>
          <Field id="presentation-contact-phone" label="Телефон" error={errors.contact_phone}>
            <PresentationInputAction
              type="tel"
              value={draft.contact_phone}
              onChange={(event) => patch({ contact_phone: event.target.value })}
              showAction={showUseProfilePhone}
              onAction={() => patch({ contact_phone: profilePhone })}
            />
          </Field>
          <Field id="presentation-contact-email" label="Email" error={errors.contact_email}>
            <PresentationInputAction
              type="email"
              value={draft.contact_email}
              onChange={(event) => patch({ contact_email: event.target.value })}
              showAction={showUseProfileEmail}
              onAction={() => patch({ contact_email: profileEmail })}
            />
          </Field>
          <PresentationToggle
            id="presentation-contact-avatar"
            label="Показувати фото профілю"
            help="Знімок для цієї презентації. Пізніші зміни в профілі його не перезапишуть."
            error={errors.include_contact_avatar}
            checked={Boolean(draft.include_contact_avatar)}
            disabled={!canEnableAvatar}
            onChange={(checked) =>
              patch({
                include_contact_avatar: checked,
                avatar_dirty: true,
              })
            }
            data-testid="presentation-avatar-toggle"
          />
          {corporateMode && !isEditing ? (
            <PresentationToggle
              id="presentation-confirm-company-fallback"
              label="Підтвердити контакт компанії як запасний"
              help="Явний дозвіл використати контакт компанії, якщо особистий контакт недоступний. Приватний телефон не підставляється автоматично."
              error={errors.confirm_company_fallback}
              checked={Boolean(draft.confirm_company_fallback)}
              onChange={(checked) => patch({ confirm_company_fallback: checked })}
              data-testid="presentation-confirm-company-fallback"
            />
          ) : null}
          <Field
            id="presentation-expiry"
            label="Термін дії"
            help="Не більше 30 днів від сьогодні."
            error={errors.expires_at}
            required
          >
            <Select
              value={draft.expiry_preset}
              options={EXPIRY_PRESETS}
              onChange={(event) =>
                patch({ expiry_preset: event.target.value, expiry_dirty: true })
              }
            />
          </Field>
          {draft.expiry_preset === "custom" ? (
            <Field id="presentation-expiry-custom" label="Дата завершення">
              <TextInput
                type="datetime-local"
                value={draft.custom_expires_at}
                onChange={(event) =>
                  patch({ custom_expires_at: event.target.value, expiry_dirty: true })
                }
              />
            </Field>
          ) : null}
        </section>
      ) : null}

      <footer className="presentation-modal__foot presentation-editor__nav">
        <button type="button" className="action-btn presentation-nav-btn" onClick={handleBack} disabled={busy}>
          Назад
        </button>
        <button type="submit" className="action-btn action-btn--primary presentation-nav-btn" disabled={busy}>
          {busy ? "Збереження…" : nextActionLabel(isLastStage, submitLabel)}
        </button>
      </footer>
    </form>
  );
}

export default PresentationEditorForm;
